import React from 'react';
import { getFileUrl } from '../../utils/fileUtil';
import { stripMarkdown } from '../../utils/markdownUtil';
import { formatEstimate } from '../../utils/timeEstimate';

const priorityColors = {
  Low: 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400 border border-green-200/20',
  Medium: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200/20',
  High: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-400 border border-yellow-200/20',
  Urgent: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400 border border-red-200/20'
};

// Parse a stored ISO date as a date-only value in local time (ignoring UTC time-of-day).
// Cards store dueDate as YYYY-MM-DDT00:00:00.000Z; treating that as a UTC timestamp
// makes a card "due today" look overdue in any timezone west of UTC.
const parseLocalDate = (date) => {
  if (!date) return null;
  const datePart = typeof date === 'string' ? date.split('T')[0] : null;
  if (datePart) {
    const [y, m, d] = datePart.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(date);
};

const formatDate = (date) => {
  const d = parseLocalDate(date);
  if (!d) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const CardItem = ({ card, onClick, onToggleComplete, bulkMode = false, isSelected = false, childCards = [], onChildClick }) => {
  const { title, description, priority, dueDate, assignedTo, createdBy, coverPhoto, labels, tags, cardType, contactInfo, socialMedia, completedAt, recurrence, parent, subtasks, comments, attachments, timeEstimate, external } = card;
  const isExternal = !!external?.source;
  const isRecurring = recurrence?.enabled === true;
  const parentTitle = parent && typeof parent === 'object' ? parent.title : null;

  const totalSubtasks = subtasks?.length || 0;
  const completedSubtasks = subtasks?.filter(st => st.completed).length || 0;
  const totalComments = comments?.length || 0;
  const totalAttachments = attachments?.length || 0;

  // A card is overdue only if its due date (date-only, local) is strictly before today.
  // Cards due today are NOT overdue.
  let isOverdue = false;
  if (dueDate) {
    const due = parseLocalDate(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    isOverdue = due < today;
  }

  const typeIcon = cardType === 'person' ? '👤' : cardType === 'company' ? '🏢' : cardType === 'social' ? '📢' : null;

  return (
    <div
      onClick={onClick}
      className={`relative bg-white dark:bg-[#141b2d] border border-gray-150 dark:border-gray-800/80 rounded-xl shadow-sm mb-2.5 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 hover:-translate-y-0.5 transition-all duration-200 ease-out cursor-pointer overflow-hidden ${isSelected ? 'ring-2 ring-primary-blue' : ''}`}
    >
      {/* Bulk selection checkbox overlay */}
      {bulkMode && (
        <div className="absolute top-2 left-2 z-10">
          <input type="checkbox" readOnly checked={isSelected} className="w-4 h-4 accent-blue-600" />
        </div>
      )}
      {/* Cover Photo */}
      {coverPhoto && (
        <div className="h-24 w-full overflow-hidden">
          <img 
            src={getFileUrl(coverPhoto)} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-3">
        {/* Type Icon & Labels */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex flex-wrap gap-1 items-center">
            {typeIcon && (
              <span className="text-[10px] bg-gray-100 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 p-1 rounded-md leading-none border border-gray-250/10" title={cardType}>
                {typeIcon}
              </span>
            )}
            {cardType === 'social' && socialMedia?.platforms?.map(p => (
               <span key={p} className="text-[8px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 px-1 rounded font-black uppercase tracking-tighter border border-blue-100 dark:border-blue-900/30">
                 {p.charAt(0)}
               </span>
            ))}
            {labels && labels.length > 0 && labels.map(label => {
              const hasText = label.text && label.text.trim() !== '';
              return hasText ? (
                <span 
                  key={label._id} 
                  className="text-[9px] font-bold px-2 py-0.5 rounded-full border leading-none"
                  style={{ 
                    backgroundColor: `${label.color}15`, 
                    color: label.color,
                    borderColor: `${label.color}30` 
                  }}
                  title={label.text}
                >
                  {label.text}
                </span>
              ) : (
                <div 
                  key={label._id} 
                  className="h-2 w-6 rounded-full" 
                  style={{ backgroundColor: label.color }}
                  title={label.text}
                />
              );
            })}
            {tags && tags.length > 0 && tags.map(tag => (
              <span
                key={tag}
                className="text-[9px] font-bold px-2 py-0.5 rounded-full border leading-none bg-gray-100 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                title={tag}
              >
                #{tag}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {isExternal && (
              <a
                href={external.url || '#'}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#00A5B1]/10 text-[#00838d] dark:text-[#4fd0da] border border-[#00A5B1]/30 flex items-center gap-0.5 hover:bg-[#00A5B1]/20"
                title={`Mirrored from Sprock${external.board ? `: ${external.board} › ${external.column}` : ''} (opens the hub)`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><circle cx="12" cy="12" r="3" strokeWidth="2.5" /></svg>
                Sprock
              </a>
            )}
            {isRecurring && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 flex items-center gap-0.5 border border-purple-200/20"
                title={`Recurring (${recurrence?.pattern || ''})`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </span>
            )}
            {priority && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${priorityColors[priority]}`}>
                {priority}
              </span>
            )}
          </div>
        </div>

        {parentTitle && (
          <div className="mb-1.5 text-[10px] text-gray-500 dark:text-gray-400 italic truncate" title={`Child of: ${parentTitle}`}>
            ↳ {parentTitle}
          </div>
        )}

        <div className="mb-2">
          <div className="flex items-start gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleComplete && onToggleComplete(card._id, completedAt); }}
              className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                completedAt
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'border-gray-300 dark:border-gray-500 hover:border-green-400 hover:bg-green-500/10'
              }`}
              title={completedAt ? 'Mark incomplete' : 'Mark complete'}
            >
              {completedAt && (
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
            <h3 className={`font-medium text-sm leading-tight ${completedAt ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>{title}</h3>
          </div>
          {cardType === 'person' && contactInfo?.companyName && (
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-550 uppercase mt-0.5">@ {contactInfo.companyName}</p>
          )}
          {cardType === 'social' && socialMedia?.postCopy && (
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-550 mt-0.5 line-clamp-1 italic">"{socialMedia.postCopy}"</p>
          )}
        </div>
        
        {description && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{stripMarkdown(description)}</p>
        )}
        
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-gray-100/50 dark:border-gray-750/50">
          {/* Due date */}
          {dueDate && (
            <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold ${
              completedAt 
                ? 'bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 border-green-200/30'
                : isOverdue 
                  ? 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 border-red-200/30' 
                  : 'bg-gray-50 dark:bg-gray-800/80 text-gray-650 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50'
            }`}>
              <span>{completedAt ? '✅' : isOverdue ? '⚠️' : '📅'}</span>
              <span>{formatDate(dueDate)}</span>
            </div>
          )}

          {/* Time estimate */}
          {timeEstimate > 0 && (
            <div className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold bg-gray-50 dark:bg-gray-800/80 text-gray-650 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50" title="Time estimate">
              <span>⏱️</span>
              <span>{formatEstimate(timeEstimate)}</span>
            </div>
          )}

          {/* Checklist progress */}
          {totalSubtasks > 0 && (
            <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold ${
              completedSubtasks === totalSubtasks
                ? 'bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 border-green-200/30'
                : 'bg-gray-50 dark:bg-gray-800/80 text-gray-650 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50'
            }`} title={`${completedSubtasks}/${totalSubtasks} Checklist Items`}>
              <span>☑️</span>
              <span>{completedSubtasks}/{totalSubtasks}</span>
            </div>
          )}

          {/* Comments count */}
          {totalComments > 0 && (
            <div className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold bg-gray-50 dark:bg-gray-800/80 text-gray-650 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50" title={`${totalComments} Comments`}>
              <span>💬</span>
              <span>{totalComments}</span>
            </div>
          )}

          {/* Attachments count */}
          {totalAttachments > 0 && (
            <div className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold bg-gray-50 dark:bg-gray-800/80 text-gray-650 dark:text-gray-300 border-gray-200/50 dark:border-gray-700/50" title={`${totalAttachments} Attachments`}>
              <span>📎</span>
              <span>{totalAttachments}</span>
            </div>
          )}

          <div className="flex items-center -space-x-1 ml-auto">
            {/* Created by (Agent/User) */}
            {createdBy && (
              <div className="h-5 w-5 rounded-full overflow-hidden border border-white dark:border-gray-800 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700" title={`Created by: ${createdBy.name}`}>
                {createdBy.avatar ? (
                  <img src={getFileUrl(createdBy.avatar)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[8px] font-black text-gray-500 dark:text-gray-400">
                    {createdBy.name?.charAt(0)}
                  </div>
                )}
              </div>
            )}

            {/* Assigned users */}
            {assignedTo && Array.isArray(assignedTo) && assignedTo.length > 0 && (
              <div className="flex items-center -space-x-2">
                {assignedTo.map(user => (
                  <div
                    key={user._id}
                    className="h-5 w-5 rounded-full bg-secondary-cyan flex items-center justify-center text-white text-[10px] border border-white dark:border-gray-800 shadow-sm relative z-10 overflow-hidden ring-2 ring-white dark:ring-gray-800"
                    title={`Assigned to: ${user.name}`}
                  >
                    {user.avatar ? (
                      <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      user.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Compact child cards */}
        {childCards && childCards.length > 0 && (
          <div className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
            {childCards
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map(ch => (
                <button
                  key={ch._id}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onChildClick && onChildClick(ch._id); }}
                  className="w-full text-left text-[11px] px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-1.5"
                  title={ch.title}
                >
                  <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full border ${ch.completedAt ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-gray-500'}`} />
                  <span className={`flex-1 truncate ${ch.completedAt ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>{ch.title}</span>
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardItem;